import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const NavBar = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userGroup, setUserGroup] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const group = localStorage.getItem("groups");
    setIsLoggedIn(!!token);
    setUserGroup(group || "");
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when tapping outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (mobileMenuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${getApiBaseUrl()}/api/users/auth/logout/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("username");
        localStorage.removeItem("groups");
        setIsLoggedIn(false);
        navigate("/");
      } else {
        alert("Logout was unsuccessful. Please try again.");
      }
    } catch (error) {
      alert("An error occurred while logging out.");
    }
  };

  const getDashboardPath = () => {
    return userGroup.includes("Owners") ? "/owner/dashboard" : "/guest/dashboard";
  };

  const menuOptions = [
    { path: "/", label: t("home") },
    { path: "/ferienpark-hahnenklee", label: t("ferienpark_hahnenklee") },
    { path: "/apartments", label: t("apartments") },
    { path: "/blog", label: t("blog") },
    { path: "/login", label: t("login") },
  ];

  const filteredMenuOptions = menuOptions.filter((option) => {
    if (option.path === location.pathname) return false;
    if (option.path === "/login" && isLoggedIn) return false;
    return true;
  });

  return (
    <nav ref={menuRef} className="fixed top-0 left-0 w-full bg-blue-500 text-white p-4 flex justify-between items-center h-16 z-50">

      {/* LEFT — LOGO */}
      <Link to="/" className="flex items-center shrink-0">
        <img
          src="/static/images/ms-icon-150x150.png"
          alt="Logo"
          className="h-10 w-10 object-contain"
        />
      </Link>

      {/* CENTER — DESKTOP MENU (hidden on mobile) */}
      <div className="hidden md:flex gap-6 items-center">
        {filteredMenuOptions.map((option) => (
          <Link key={option.path} to={option.path} className="px-4 hover:text-blue-200 transition-colors">
            {option.label}
          </Link>
        ))}

        {isLoggedIn && !location.pathname.includes("/dashboard") && (
          <Link to={getDashboardPath()} className="px-4 hover:text-blue-200 transition-colors">
            {t("dashboard")}
          </Link>
        )}
      </div>

      {/* RIGHT — LOGOUT (desktop) + HAMBURGER (mobile) */}
      <div className="flex items-center gap-3">
        {isLoggedIn && (
          <button onClick={handleLogout} className="hidden md:block bg-red-500 px-4 py-2 rounded hover:bg-red-600 transition-colors">
            {t("logout")}
          </button>
        )}

        {/* Hamburger button — visible on mobile only */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5"
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${mobileMenuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block w-6 h-0.5 bg-white transition-opacity duration-300 ${mobileMenuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      {mobileMenuOpen && (
        <div className="absolute top-16 left-0 w-full bg-blue-600 md:hidden flex flex-col shadow-lg z-50">
          {filteredMenuOptions.map((option) => (
            <Link
              key={option.path}
              to={option.path}
              className="px-6 py-3 border-b border-blue-500 hover:bg-blue-700 transition-colors"
            >
              {option.label}
            </Link>
          ))}

          {isLoggedIn && !location.pathname.includes("/dashboard") && (
            <Link to={getDashboardPath()} className="px-6 py-3 border-b border-blue-500 hover:bg-blue-700 transition-colors">
              {t("dashboard")}
            </Link>
          )}

          {isLoggedIn && (
            <button
              onClick={handleLogout}
              className="px-6 py-3 text-left bg-red-500 hover:bg-red-600 transition-colors"
            >
              {t("logout")}
            </button>
          )}
        </div>
      )}
    </nav>
  );
};

export default NavBar;
